import { CalendarRange, Clock, TrendingUp } from "lucide-react";
import { useState } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { PendingBanner } from "@/features/presence/components/pending-banner";
import {
	PunchCard,
	PunchCardSkeleton,
} from "@/features/presence/components/punch-card";
import {
	WeekTable,
	WeekTableSkeleton,
} from "@/features/presence/components/week-table";
import { useMyAttendanceDays } from "@/features/presence/hooks/use-attendance-days";
import { useMyMembership } from "@/features/presence/hooks/use-my-membership";
import { useNow } from "@/features/presence/hooks/use-now";
import {
	countDaysByStatus,
	dayWorkedMinutes,
	sumTotals,
} from "@/features/presence/utils/attendance";
import { hasOpenEntry } from "@/features/presence/utils/day";
import {
	formatMinutes,
	formatSignedMinutes,
} from "@/features/presence/utils/format";
import {
	dayRange,
	shiftWeeks,
	weekRange,
} from "@/features/presence/utils/week";
import { KpiCard } from "@/features/team/components/kpi-card";
import { usePermissions } from "@/hooks/use-permissions.ts";
import { currentWorkDate, currentWorkDay } from "@/lib/format";

function todayKpiSub(intervals: number): string {
	if (intervals === 0) return "Nenhum registro hoje";
	if (intervals === 1) return "Registro do dia";
	return `${intervals} intervalos hoje`;
}

export function PresencePage() {
	const [reference, setReference] = useState(() => currentWorkDay());
	const { has, isLoading: permsLoading } = usePermissions();

	const canClock = has("time_entries:clock");
	const canList = has("time_entries:list");
	const canView = canClock || canList;

	const today = currentWorkDate();
	const range = weekRange(reference);
	const weekQuery = useMyAttendanceDays(range, canView);
	const todayQuery = useMyAttendanceDays(dayRange(currentWorkDay()), canView);
	const {
		worker,
		isMember,
		isLoading: membershipLoading,
		isError: membershipError,
	} = useMyMembership();

	const weekDays = weekQuery.data?.[0]?.days ?? [];
	const todayDay = todayQuery.data?.[0]?.days.find((day) => day.date === today);
	const todayEntries = todayDay?.entries ?? [];
	const now = useNow(hasOpenEntry(todayEntries));

	const todayMinutes = todayDay ? dayWorkedMinutes(todayDay, now) : 0;
	const weekMinutes = weekDays.reduce(
		(total, day) => total + dayWorkedMinutes(day, now),
		0,
	);
	const weekTotals = sumTotals(weekDays);
	const presentDays = countDaysByStatus(weekDays, "present");

	const isLoading =
		permsLoading ||
		membershipLoading ||
		weekQuery.isPending ||
		todayQuery.isPending;

	function body() {
		if (!canView && !permsLoading) {
			return (
				<Card>
					<CardContent className="card-state">
						<p>Você não tem permissão para registrar ponto nesta obra.</p>
					</CardContent>
				</Card>
			);
		}

		if (isLoading) {
			return (
				<>
					<PunchCardSkeleton />
					<WeekTableSkeleton />
				</>
			);
		}

		if (
			weekQuery.isError ||
			todayQuery.isError ||
			membershipError ||
			isMember === undefined
		) {
			return (
				<Card>
					<CardContent className="card-state">
						<p>Não foi possível carregar os seus registros de ponto.</p>
					</CardContent>
				</Card>
			);
		}

		return (
			<>
				<PunchCard
					entries={todayEntries}
					isMember={isMember}
					timeClockEnabled={worker?.timeClockEnabled ?? false}
					canClock={canClock}
				/>

				<div className="presence-kpis">
					<KpiCard
						icon={Clock}
						accent="primary"
						label="Hoje"
						value={formatMinutes(todayMinutes)}
						sub={todayKpiSub(todayEntries.length)}
					/>
					<KpiCard
						icon={CalendarRange}
						accent="success"
						label="Esta semana"
						value={formatMinutes(weekMinutes)}
						sub={`${presentDays} ${presentDays === 1 ? "dia presente" : "dias presentes"}`}
					/>
					<KpiCard
						icon={TrendingUp}
						accent="warning"
						label="Extras na semana"
						value={formatSignedMinutes(weekTotals.overtimeMinutes)}
						sub={`Equivalente ${formatSignedMinutes(weekTotals.overtimeEquivalentMinutes)}`}
					/>
				</div>

				<WeekTable
					reference={reference}
					days={weekDays}
					now={now}
					onPrevious={() => setReference((current) => shiftWeeks(current, -1))}
					onNext={() => setReference((current) => shiftWeeks(current, 1))}
				/>
			</>
		);
	}

	return (
		<div className="presence-page">
			<div className="page-header">
				<div>
					<h1 className="page-title">Presença</h1>
					<p className="page-subtitle">
						Registre seu ponto e acompanhe suas horas trabalhadas.
					</p>
				</div>
			</div>

			<PendingBanner />

			{body()}
		</div>
	);
}
